import { createAction } from '@reduxjs/toolkit'
import Axios from 'axios'
import InventoryItem from '../../Components/InventoryItem/InventoryItem'
import { InventoryItemsManifest, InventoryItemsManifestJson } from '../../API/InventoryItemsManifestJson'
import { Resolution } from '../../Controllers/ResolutionController'
import dispatch from '../../Utility/dispatchAction'
import Config from '../Config'
import { validateInventory } from '../../API/validation'
import GameSceneContainer from '../GameSceneContainer/GameSceneContainer'
import LoadingController from '../../Controllers/LoadingController'

export enum InventoryActionType {
    removed = 'removed',
    added = 'added',
}

const getEventType = (type: InventoryActionType) => `Inventory.${type}`

export const inventoryItemActionCreator = (type: InventoryActionType, itemId: string) =>
    createAction(getEventType(type), () => ({
        payload: {
            itemId,
        },
    }))()

export const createInventoryActionListener = (
    type: InventoryActionType,
    callback?: (itemId: string) => void,
    once = false
): Promise<void> =>
    new Promise<void>((resolve) => {
        const handler: EventListener = (event) => {
            // @ts-ignore -- WHY: CustomEvent
            const id = event.detail.itemId as string

            callback && callback(id)
            resolve()
        }

        addEventListener(getEventType(type), handler, { once })
    })

export const createInventoryItemActionListener = (
    itemId: string,
    type: InventoryActionType,
    callback?: (itemId: string) => void,
    once = false
): Promise<void> =>
    new Promise<void>((resolve) => {
        const handler: EventListener = (event) => {
            // @ts-ignore -- WHY: CustomEvent
            const id = event.detail.itemId as string

            if (itemId === id) {
                once && removeEventListener(getEventType(type), handler)
                callback && callback(itemId)
                resolve()
            }
        }

        addEventListener(getEventType(type), handler)
    })

// TODO InventoryController

class InventoryContainer {
    element = document.querySelector('#inventory') as HTMLDivElement
    itemsContainerElement = this.element.querySelector('#inventory-items-container') as HTMLDivElement

    itemManifests: InventoryItemsManifest = {}
    loadedInventoryItems: Record<string, InventoryItem> = {}
    itemsInInventory: Array<string> = []

    constructor() {
        this.hide()
        this.outOfView()

        this.element.onmouseover = this.intoView
        this.element.onmouseout = this.outOfView

        window.addEventListener('resize', () => this.updateHeight())
    }

    load = async () => {
        const response = await Axios.get(`${Config.ASSET_PATH}inventory.json`)

        const manifest: InventoryItemsManifestJson = response.data

        if (!validateInventory) {
            throw new Error('Inamove::InventoryCombinationsController => could not get validation!')
        }

        if (!validateInventory(manifest)) {
            console.error(
                `Inamove::InventoryCombinationsController => Manifest validation failed:`,
                validateInventory.errors
            )
            throw new Error()
        }

        const withCorrectUrls: InventoryItemsManifest = Object.keys(manifest).reduce<InventoryItemsManifest>(
            (acc, itemId) => ({
                ...acc,
                [itemId]: {
                    ...manifest[itemId],
                    thumbnailURL: Config.INVENTORY_IMAGE_PATH + manifest[itemId].thumbnailURL,
                    detailImageURL: Config.INVENTORY_IMAGE_PATH + manifest[itemId].detailImageURL,
                    infoAudioURL: Config.INVENTORY_AUDIO_PATH + manifest[itemId].infoAudioURL + Config.AUDIO_FILE_EXT,
                    detailAudioURL:
                        Config.INVENTORY_AUDIO_PATH + manifest[itemId].detailAudioURL + Config.AUDIO_FILE_EXT,
                },
            }),
            {}
        )

        this.itemManifests = withCorrectUrls
    }

    loadItems = (itemIds: Array<string>) => {
        if (itemIds.length === 0) {
            return
        }

        console.debug(`Inamove::InventoryContainer => load items: ${itemIds}`)

        const itemsToLoad = itemIds.filter((itemId) => this.loadedInventoryItems[itemId] === undefined)
        const newItems = itemsToLoad.map((itemId) => {
            const item = new InventoryItem(this.itemManifests[itemId])
            LoadingController.addToQueue(item)
            return item
        })

        this.loadedInventoryItems = newItems.reduce(
            (acc, item) => ({
                ...acc,
                [item.id]: item,
            }),
            this.loadedInventoryItems
        )
        console.debug(`Inamove::InventoryContainer => loaded items ${itemIds}`)
    }

    isItemInInventory = (itemId: string) => this.itemsInInventory.some((id) => id === itemId)

    addItems = async (itemIds: Array<string>, showFeedback = true) => {
        const itemIdsToAdd = itemIds.filter((itemId) => !this.isItemInInventory(itemId))

        this.itemsInInventory = [...this.itemsInInventory, ...itemIdsToAdd]

        const nodesToAdd = itemIdsToAdd.map((itemId) => {
            const element = this.loadedInventoryItems[itemId].element

            if (showFeedback) {
                element.classList.add('newly-added')
            }

            return element
        })

        this.itemsContainerElement.prepend(...nodesToAdd)

        nodesToAdd.forEach((item) => {
            dispatch(inventoryItemActionCreator(InventoryActionType.added, item.id))
        })

        if (showFeedback) {
            this.showFeedback()
        }

        this.updateHeight()
    }

    removeItems = (itemIds: Array<string>) => {
        const itemsToRemove = itemIds.filter((itemId) => this.isItemInInventory(itemId))

        itemsToRemove.forEach((itemId) => {
            this.loadedInventoryItems[itemId].remove()
            delete this.loadedInventoryItems[itemId]

            this.itemsInInventory = this.itemsInInventory.filter((id) => id !== itemId)

            dispatch(inventoryItemActionCreator(InventoryActionType.removed, itemId))
        })

        this.updateHeight()
    }

    clean = () => {
        this.itemsInInventory.forEach((itemId) => this.loadedInventoryItems[itemId].remove())
        this.itemsInInventory = []
        this.updateHeight()
    }

    show = () => {
        this.element.classList.remove('hidden')
    }

    hide = () => {
        this.element.classList.add('hidden')
    }

    outOfView = () => {
        this.element.classList.add('out-of-view')
        GameSceneContainer.removeClassName('inventory-open')
    }

    intoView = () => {
        this.element.classList.remove('out-of-view')
        GameSceneContainer.addClassName('inventory-open')
    }

    showFeedback = () => {
        if (this.element.classList.contains('out-of-view')) {
            this.element.addEventListener('animationend', () => {
                this.element.classList.remove('feedback')
            })
            this.element.classList.add('feedback-animation')
        }
    }

    updateHeight = () => {
        // TODO: is there a better way using only css?
        const itemCount = this.itemsInInventory.length
        const colCount = Config.resolution.resolution === Resolution.small ? 6 : 10
        const rowCount = Math.ceil(itemCount / colCount)

        console.debug(`Inamove::InventoryItemContainer => `, {
            colCount,
            itemCount,
            rowCount,
        })

        const itemHeight = 90
        const itemPadding = 5

        const translateY = `translateY(${rowCount * itemHeight + (rowCount - 1) * itemPadding}px)`

        // We manipulate the parsed CSS file directly. Ugh.
        // TODO: find another solution
        for (let i = 0; i < document.styleSheets.length; i++) {
            const sheet = document.styleSheets[i]
            if (sheet && sheet.href && (sheet.href.includes('Game') || sheet.href.includes('test'))) {
                const cssRules = Array.from(sheet.cssRules) as Array<CSSPageRule>
                const rule = cssRules.filter((rule) => rule.selectorText === '#inventory.out-of-view')[0]
                rule.style.setProperty('transform', translateY)
            }
        }
    }
}

export default new InventoryContainer()
